/**
 * Sensitivity cog — tunes the peer-percentile threshold used to flag a
 * site's variance against its peer cohort on the Site Variance detail.
 *
 * Ephemeral FE-only state: lives in `variance-route.tsx` (`useState(75)`),
 * resets on reload, and is threaded to the request as `t_peer` via
 * `useGetVariance`. See `../../data/use-get-variance.ts` for the wire
 * contract — this component only renders the control and reports the
 * chosen value up via `onChange`.
 *
 * The Slider's `onChange` fires on every intermediate drag tick — that's
 * only mirrored into local `liveValue` (keeps the number/hint responsive
 * while dragging). The parent's `onChange` (-> a `t_peer` refetch) fires
 * exactly once, on `onChangeCommitted` (pointer release / keyboard commit),
 * so scrubbing the slider doesn't spawn a refetch per tick.
 */
import type React from "react"
import { useId, useState } from "react"

import { Button } from "@/nzds/inputs/button"

import { SettingsRounded } from "@mui/icons-material"
import {
  IconButton,
  Popover,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"

export const SENSITIVITY_DEFAULT = 75
const SENSITIVITY_MIN = 50
const SENSITIVITY_MAX = 99

export interface SensitivityCogProps {
  value: number
  onChange: (value: number) => void
}

export const SensitivityCog: React.FC<SensitivityCogProps> = ({
  value,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [liveValue, setLiveValue] = useState(value)
  // Tracks the `value` a render's `liveValue` was derived from, so a
  // changed `value` prop can be mirrored into `liveValue` during render
  // (React's documented "adjusting state when a prop changes" escape
  // hatch) rather than via a useEffect, which would cascade an extra
  // render. The only way `value` changes is via this component's own
  // `onChange` calls (commit or reset), so this never fires mid-drag —
  // `value` itself doesn't change until commit.
  const [committedValue, setCommittedValue] = useState(value)
  if (value !== committedValue) {
    setCommittedValue(value)
    setLiveValue(value)
  }
  const popoverId = useId()
  const open = Boolean(anchorEl)

  return (
    <>
      <Tooltip title="Sensitivity">
        <IconButton
          aria-label="Sensitivity"
          onClick={(event) => {
            setAnchorEl(event.currentTarget)
          }}
          size="small"
        >
          <SettingsRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        id={open ? popoverId : undefined}
        onClose={() => {
          setAnchorEl(null)
        }}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <Stack spacing={2} sx={{ p: 2.5, width: 280 }}>
          <Stack spacing={0.25}>
            <Typography sx={{ fontWeight: "bold" }} variant="body1">
              Peer sensitivity
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Showing sites ≥ {liveValue}th percentile vs peers
            </Typography>
          </Stack>

          <Slider
            aria-label="Peer percentile sensitivity"
            max={SENSITIVITY_MAX}
            min={SENSITIVITY_MIN}
            onChange={(_event: Event, newValue: number | number[]) => {
              setLiveValue(Array.isArray(newValue) ? newValue[0] : newValue)
            }}
            onChangeCommitted={(
              _event: React.SyntheticEvent | Event,
              newValue: number | number[]
            ) => {
              onChange(Array.isArray(newValue) ? newValue[0] : newValue)
            }}
            size="small"
            step={1}
            value={liveValue}
            valueLabelDisplay="auto"
          />

          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            <Button
              disabled={liveValue === SENSITIVITY_DEFAULT}
              onClick={() => {
                setLiveValue(SENSITIVITY_DEFAULT)
                onChange(SENSITIVITY_DEFAULT)
              }}
              size="small"
              variant="text"
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
